import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  GetCBUListProps,
  GetCBUListResponse,
} from "@/services/cbu.service";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { cbuFilterSchema } from "./schema/cbu.schema";
import { CBUFilter } from "@/components/common/section-components/filters/CbuFilters";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { CBUHeaderElements } from "./schema/header-elements";
import { StandardTable } from "@/components/table/StandardTable";
import {
  clearFiltersHandler,
  fetchCbusHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { NoData } from "@/components/common/section-components/NoData";
import { AppRoutes } from "@/constants/routes";
import { usePolling } from "@/hooks/usePolling";
import { useRoles } from "@/hooks/useRoles";
export const CBUPage = () => {
  const navigate = useNavigate();

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [isActive, setIsActive] = useState<boolean>(false);

  const [selection, setSelection] = useState<string[]>([]);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [loadingCBU, setLoadingCBU] = useState<boolean>(false);
  const [cbus, setCbus] = useState<GetCBUListResponse | null>({
    listado: [],
    totalPages: 1,
    totalRecords: 10,
  });

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<GetCBUListProps>({});
  const { control, handleSubmit, setValue, getValues } = useForm({
    resolver: zodResolver(cbuFilterSchema),
  });

  const fetchWithParams = async (params: { cbu: string }) => {
    setValue("search", params?.cbu);
    setFiltersApplied(true);
    await fetchCBUs({
      busqueda: params?.cbu,
    });
  };

  const fetchCBUs = async (filters?: GetCBUListProps, showLoad = true) => {
    await fetchCbusHandler({
      setLoadingCBU,
      setCbus,
      filters,
      navigate,
      fetchPermissions,
      showLoad,
    });
  };

  useEffect(() => {
    if (isActive) {
      const cbu = searchParams.get("cbu") ?? undefined;
      if (cbu) {
        fetchWithParams({
          cbu,
        });
      } else {
        fetchCBUs();
      }
    }
  }, [isActive]);

  usePolling({
    callback: () => fetchCBUs({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive,
  });

  const { fetchPermissions } = useRoles();

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchCBUs,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
    });
  };

  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchCBUs({ ...currentFilters, "P.Pagina": page });
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={false}
        title="Maestro de CBU"
        loading={loadingCBU}
        backButton={
          Boolean(searchParams.get("cbu")) &&
          location?.state?.from === AppRoutes.ACCOUNTS_DETAIL
        }
        onBack={handleBackButton}
      />

      <CBUFilter
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        filtersApplied={filtersApplied}
      />

      <Flex flexDir="column" paddingX="20px">
        {Boolean(!loadingCBU && !cbus?.listado?.length) && <NoData />}
        {Boolean(!loadingCBU && cbus?.listado?.length) && (
          <>
            <StandardTable
              noSelection
              items={cbus?.listado || []}
              headers={CBUHeaderElements}
              noActions
              selection={selection}
              setSelection={setSelection}
            />
            <PaginationComponent
              page={page}
              handlePageChange={handlePageChange}
              pageSize={10}
              count={cbus?.totalRecords ?? 0}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
